import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import MetaControl from 'Components/UI/Meta/MetaControl';
import {
  FDAccordion, FDAccordionDetails, FDAccordionTitle, FDStack,
} from '@facet-wealth/design';
import { buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import { getBorderRadius } from 'Components/utils';
import useMobileLayout from 'Hooks/useMobileLayout';
import { ImpactPicker } from 'Views/Page/Content/Templates/planSummaryPageTemplate/step/impact';
import { dataAttrs } from 'Helpers/Testing';
import { normalStepOverrides } from './normalStepOverrides';
import { StepActions } from './actions';

const useStyles = buildUseStyles('NormalStep', (theme) => ({
  root: {
    backgroundColor: theme.palette.common.white, // Visually differentiate the accordion
    ...getBorderRadius(theme.radius.md, { all: true }),
    '& .FD-MuiAccordionSummary-root': {
      padding: theme.spacing(1, 4),
      '& .FD-MuiTypography-root': {
        transition: theme.transitions.create('font-size', {
          easing: theme.transitions.easing.easeInOut,
          duration: theme.transitions.duration.standard,
        }),
      },
    },
    '& .FD-MuiAccordionDetails-root > *': {
      padding: theme.spacing(0, 2, 1, 2),
    },
  },
}));

export function NormalStep({ resource, expanded, onExpandedChange }) {
  const classes = useStyles();
  const impacts = useMemo(() => resource.attr('impacts').resources().list(), [resource]);
  const isMobile = useMobileLayout();
  return (
    <div className={classNames(classes.root, expanded ? classes.expanded : classes.collapsed)}>
      <FDAccordion expanded={expanded} onChange={onExpandedChange} {...dataAttrs('normal-step', resource)}>
        <FDAccordionTitle>
          <FDStack direction="row" justifyContent="space-between" flex={1} spacing={1} wrap>
            <MetaControl resource={resource} metaOverrideSet={normalStepOverrides} attrName="title" />
            <MetaControl resource={resource} metaOverrideSet={normalStepOverrides} attrName="pillars" />
          </FDStack>
        </FDAccordionTitle>
        <FDAccordionDetails>
          <FDStack direction={isMobile ? 'column' : 'row'} spacing={isMobile ? 3 : 4} justifyContent="space-between">
            <FDStack direction="column" spacing={2} flex={4}>
              <MetaControl resource={resource} metaOverrideSet={normalStepOverrides} attrName="description" />
              <StepActions collection={resource.attr('actions')} label={resource.metaField('actions').attr('prompt')} />
            </FDStack>
            {impacts.map(impact => (
              <ImpactPicker key={impact?.id} resource={impact} />
            ))}
          </FDStack>
        </FDAccordionDetails>
      </FDAccordion>
    </div>
  );
}
NormalStep.defaultProps = {
};

NormalStep.propTypes = {
  resource: PropTypes.object.isRequired,
  expanded: PropTypes.bool.isRequired,
  onExpandedChange: PropTypes.func.isRequired,
};
